<template lang="pug">
    AiToolsTabs.chatbot-conversation-detail
        el-card(shadow="always" v-loading="currentConversationLoading")
            .error(v-if="error")
                Error(:message="$t('Something went wrong!')" :code="501")
            .conversation-detail(v-else-if="conversation")
                .conversation-header
                    el-descriptions(:column="2" border size="small")
                        el-descriptions-item(:label="$t('Session ID')")
                            span.session-id {{ conversation.session_id }}
                        el-descriptions-item(:label="$t('IP Address')")
                            | {{ conversation.ip_address }}
                        el-descriptions-item(:label="$t('Messages')")
                            | {{ conversation.message_count }}
                        el-descriptions-item(:label="$t('Status')")
                            el-tag(
                                :type="isActive ? 'success' : 'info'"
                                size="small"
                            ) {{ isActive ? $t('Active') : $t('Idle') }}
                        el-descriptions-item(:label="$t('Created')")
                            | {{ formatDate(conversation.created_at) }}
                        el-descriptions-item(:label="$t('Expires')")
                            | {{ conversation.expires_at ? formatDate(conversation.expires_at) : '—' }}
                        el-descriptions-item(:label="$t('Input tokens')")
                            | {{ totalInputTokens.toLocaleString() }}
                        el-descriptions-item(:label="$t('Output tokens')")
                            | {{ totalOutputTokens.toLocaleString() }}
                        el-descriptions-item(:label="$t('Usage costs')")
                            span(:title="$t('What the answers of this chat add to the invoice, at the price of the moment each was given. The invoice itself rounds per billing period and adds recurring fees.')") {{ totalBilledCost === null ? '—' : formatCost(totalBilledCost) }}

                .context-section(v-if="conversation.context")
                    el-button.context-toggle(
                        plain
                        size="mini"
                        :icon="showContext ? 'el-icon-fa-chevron-down fas' : 'el-icon-fa-chevron-right fas'"
                        @click="showContext = !showContext"
                    )
                        | {{ showContext ? $t('Hide conversation context') : $t('Show conversation context') }}
                    .context-body(v-if="showContext")
                        pre {{ conversation.context }}

                .messages-container
                    .message-bubble(
                        v-for="message in conversation.messages"
                        :key="message.id"
                        :class="'message-bubble--' + message.role"
                    )
                        .message-bubble__header
                            span.message-bubble__role {{ $t(message.role) }}
                            span.message-bubble__time {{ formatDate(message.created_at) }}
                        .message-bubble__content.message-bubble__content--markdown(
                            v-if="message.role === 'assistant'"
                            v-html="renderMarkdown(message.content)"
                        )
                        .message-bubble__content.message-bubble__content--plain(v-else) {{ message.content }}
                        .product-cards(v-if="productCards(message).length")
                            a.product-card(
                                v-for="card in productCards(message)"
                                :key="card.slug"
                                :href="card.productUrl"
                                target="_blank"
                                rel="noopener"
                            )
                                .product-card__image
                                    img(v-if="card.imageUrl" :src="card.imageUrl" :alt="card.name" loading="lazy" @error="hideBrokenImage")
                                .product-card__info
                                    .product-card__name {{ card.name }}
                                    .product-card__price(v-if="card.price") {{ card.price }}
                                    .product-card__availability(:class="card.available ? 'product-card__availability--in-stock' : 'product-card__availability--out-of-stock'")
                                        span.product-card__dot
                                        span {{ card.available ? $t('In stock') : $t('Not available') }}
                        .message-bubble__footer(v-if="hasTokenInfo(message) || traces[message.id]")
                            span.token-stat(v-if="message.input_tokens")
                                i.el-icon-fa-sign-in-alt.fas
                                |  {{ message.input_tokens }} {{ $t('in') }}
                            span.token-stat(v-if="message.output_tokens")
                                i.el-icon-fa-sign-out-alt.fas
                                |  {{ message.output_tokens }} {{ $t('out') }}
                            span.token-stat(v-if="hasBilledCost(message)" :title="$t('What this answer adds to the invoice')")
                                i.el-icon-fa-coins.fas
                                |  {{ formatCost(message.billed_eur) }}
                            el-tag.corrected-tag(v-if="wasCorrected(message)" size="mini" type="warning") {{ $t('Answer corrected') }}
                            el-button.trace-toggle(
                                v-if="traces[message.id]"
                                type="text"
                                size="mini"
                                @click="toggleTrace(message.id)"
                            ) {{ isTraceOpen(message.id) ? $t('Hide trace') : $t('Show trace') }}
                        .trace(v-if="traces[message.id] && isTraceOpen(message.id)")
                            .trace__notice(v-if="traces[message.id].legacy") {{ $t('This answer was written before the trace was recorded. Only the stored sources are shown.') }}
                            section.trace__section(v-if="traces[message.id].router")
                                h4.trace__label {{ $t('Search') }}
                                .trace__body
                                    span.trace__query {{ traces[message.id].router.standalone_query }}
                                    span.badge(:class="traces[message.id].router.needs_products ? 'badge--success' : 'badge--neutral'") {{ traces[message.id].router.needs_products ? $t('With products') : $t('Without products') }}
                                    span.badge.badge--warning(v-if="traces[message.id].router.fallback") {{ $t('Router failed') }}
                            section.trace__section
                                h4.trace__label {{ $t('Sources') }}
                                .trace__body
                                    span.trace__empty(v-if="!traces[message.id].sources || !traces[message.id].sources.length") {{ $t('None') }}
                                    ul.trace__sources(v-else)
                                        li(
                                            v-for="(source, index) in traces[message.id].sources"
                                            :key="index"
                                            tabindex="0"
                                            role="button"
                                            @click="openSource(message, index)"
                                            @keydown.enter.prevent="openSource(message, index)"
                                        )
                                            span.trace__kind {{ sourceKind(source.type) }}
                                            span.trace__source-title(:title="source.title") {{ source.title || '-' }}
                                            span.trace__score(v-if="source.score") {{ formatScore(source.score) }}
                            section.trace__section(v-if="traces[message.id].toolCalls && traces[message.id].toolCalls.length")
                                h4.trace__label {{ $t('Tools') }}
                                .trace__body
                                    .trace__tool(v-for="(call, index) in traces[message.id].toolCalls" :key="index")
                                        code.trace__tool-name {{ call.name }}
                                        span.trace__arguments(v-if="call.arguments && call.arguments.length") {{ formatArguments(call.arguments) }}
                                        pre.trace__excerpt(v-if="call.excerpt") {{ formatExcerpt(call.excerpt) }}
                            section.trace__section(v-if="traces[message.id].evidence && traces[message.id].evidence.length")
                                h4.trace__label {{ $t('Evidence') }}
                                .trace__body
                                    ul.trace__evidence
                                        li(v-for="(item, index) in traces[message.id].evidence" :key="index")
                                            span.trace__kind {{ evidenceKind(item.kind) }}
                                            code.trace__fact {{ item.text }}
                                            span.trace__found(v-if="item.foundIn && item.foundIn.length") {{ item.foundIn.join(', ') }}
                                            span.badge.badge--danger(v-else) {{ $t('Not found in the data of this answer') }}
                            section.trace__section(v-if="!traces[message.id].legacy")
                                h4.trace__label {{ $t('Corrections') }}
                                .trace__body
                                    span.trace__empty(v-if="!traces[message.id].guard || !traces[message.id].guard.length") {{ $t('None') }}
                                    .trace__correction(v-for="(action, index) in traces[message.id].guard" :key="index")
                                        span.badge(:class="action.action === 'repaired' ? 'badge--warning' : 'badge--danger'") {{ actionLabel(action) }}
                                        .trace__context(v-if="action.line")
                                            .trace__neighbour(v-if="action.previous") {{ action.previous }}
                                            .trace__diff.trace__diff--removed
                                                span(v-for="(part, partIndex) in highlightParts(action.line, action.original)" :key="partIndex" :class="{ 'trace__hit': part.hit }") {{ part.text }}
                                            .trace__diff.trace__diff--added(v-if="action.result")
                                                span(v-for="(part, partIndex) in highlightParts(action.result, action.replacement)" :key="partIndex" :class="{ 'trace__hit': part.hit }") {{ part.text }}
                                            .trace__diff.trace__diff--gone(v-else) {{ $t('Line removed') }}
                                            .trace__neighbour(v-if="action.next") {{ action.next }}
                                        template(v-else)
                                            .trace__diff.trace__diff--removed {{ action.original }}
                                            .trace__diff.trace__diff--added(v-if="action.replacement") {{ action.replacement }}
                            section.trace__section(v-if="traces[message.id].model")
                                h4.trace__label {{ $t('Model') }}
                                .trace__body.trace__meta
                                    span {{ traces[message.id].model }}
                                    span(v-if="traces[message.id].system_prompt_hash") {{ $t('Prompt') }} {{ traces[message.id].system_prompt_hash.slice(0, 8) }}
            .empty(v-else-if="!currentConversationLoading")
                | {{ $t('Conversation not found.') }}

        .form-buttons
            el-button(
                round
                icon="el-icon-fa-times fas"
                @click="goBack"
            ) {{ $t('Dismiss') }}

        full-height-drawer(
            :visible.sync="sourceDrawerVisible"
            :title="openedSource ? (openedSource.title || sourceKind(openedSource.type)) : ''"
            size="560px"
            :append-to-body="true"
            :wrapper-closable="true"
        )
            template(v-if="openedSource")
                dl.source-meta
                    dt {{ $t('Kind') }}
                    dd {{ sourceKind(openedSource.type) }}
                    template(v-if="openedSource.score")
                        dt {{ $t('Match') }}
                        dd {{ formatScore(openedSource.score) }}
                    template(v-if="openedSource.url")
                        dt {{ $t('Link') }}
                        dd
                            a(:href="openedSource.url" target="_blank" rel="noopener") {{ openedSource.url }}
                h4.source-content-label {{ $t('Text the model was given') }}
                pre.source-content(v-if="openedSource.content") {{ openedSource.content }}
                .source-missing(v-else-if="openedSourceLoading")
                    i.el-icon-loading
                .source-missing(v-else) {{ $t('The text of this source was not stored for this answer.') }}
            template(#footer-left)
                el-button(round size="mini" icon="el-icon-fa-times fas" @click="sourceDrawerVisible = false") {{ $t('Close') }}
</template>

<i18n>
{
    "en": {
        "user": "User",
        "assistant": "Assistant",
        "in": "in",
        "out": "out"
    },
    "de": {
        "Session ID": "Session-ID",
        "IP Address": "IP-Adresse",
        "Messages": "Nachrichten",
        "Status": "Status",
        "Active": "Aktiv",
        "Idle": "Inaktiv",
        "Created": "Erstellt",
        "Expires": "Läuft ab",
        "Conversation not found.": "Konversation nicht gefunden.",
        "user": "Nutzer",
        "assistant": "Assistent",
        "Something went wrong!": "Etwas ist schiefgelaufen!",
        "Dismiss": "Verwerfen",
        "Show conversation context": "Konversations-Kontext anzeigen",
        "Hide conversation context": "Konversations-Kontext verbergen",
        "Answer corrected": "Antwort korrigiert",
        "This answer was written before the trace was recorded. Only the stored sources are shown.": "Diese Antwort entstand, bevor der Ablauf aufgezeichnet wurde. Gezeigt werden nur die gespeicherten Quellen.",
        "Show trace": "Details anzeigen",
        "Hide trace": "Details verbergen",
        "Search": "Suche",
        "With products": "Mit Produkten",
        "Without products": "Ohne Produkte",
        "Router failed": "Standardsuche",
        "Sources": "Quellen",
        "Tools": "Tools",
        "Corrections": "Korrekturen",
        "None": "Keine",
        "Line removed": "Zeile entfernt",
        "Close": "Schließen",
        "Evidence": "Belege",
        "Not found in the data of this answer": "In den Daten dieser Antwort nicht gefunden",
        "Price": "Preis",
        "E-mail": "E-Mail",
        "Phone": "Telefon",
        "Kind": "Art",
        "Match": "Treffer",
        "Link": "Link",
        "Text the model was given": "Text, wie ihn das Modell erhielt",
        "The text of this source was not stored for this answer.": "Der Text dieser Quelle wurde für diese Antwort nicht gespeichert.",
        "System prompt": "Systemprompt",
        "Page": "Seite",
        "Product": "Produkt",
        "Event": "Termin",
        "Entry": "Eintrag",
        "Business card": "Visitenkarte",
        "Address": "Adresse",
        "Section": "Abschnitt",
        "Source": "Quelle",
        "Model": "Modell",
        "Usage costs": "Nutzungskosten",
        "What the answers of this chat add to the invoice, at the price of the moment each was given. The invoice itself rounds per billing period and adds recurring fees.": "Was die Antworten dieses Chats zur Rechnung hinzufügen, zum Preis des Moments, in dem sie gegeben wurden. Die Rechnung selbst rundet je Abrechnungszeitraum und enthält die wiederkehrenden Gebühren.",
        "What this answer adds to the invoice": "Was diese Antwort zur Rechnung hinzufügt",
        "In stock": "Auf Lager",
        "Not available": "Nicht verfügbar",
        "Prompt": "Prompt",
        "Link repaired": "Link repariert",
        "Link kept as text": "Link als Text behalten",
        "Link removed": "Link entfernt",
        "Detail removed": "Angabe entfernt",
        "Input tokens": "Eingabe-Tokens",
        "Output tokens": "Ausgabe-Tokens",
        "in": "ein",
        "out": "aus",
        "yyyy-MM-dd": "dd.MM.yyyy"
    }
}
</i18n>

<script>
import DOMPurify from 'dompurify';
import { marked } from 'marked';

import { timestamp2date } from '@/filters';
import { useChatbotConversationStore } from '@/stores';

import Error from '@/components/Error';
import FullHeightDrawer from '@/components/FullHeightDrawer';
import AiToolsTabs from '@/components/AiToolsTabs';

marked.setOptions({
    breaks: true,
    gfm: true
});

export default {
    name: 'ChatbotConversationDetail',

    components: {
        Error,
        AiToolsTabs,
        'full-height-drawer': FullHeightDrawer
    },

    data() {
        return {
            error: false,
            showContext: false,
            openTraces: {},
            sourceDrawerVisible: false,
            openedSource: null,
            openedSourceKey: null,
            openedSourceLoading: false,
            sourceTexts: {}
        };
    },

    computed: {
        // Every assistant answer with something to show gets a trace panel. Answers written before the trace existed only know the sources of the customer turn.
        traces() {
            const messages = (this.conversation && this.conversation.messages) || [];
            return messages.reduce((traces, message, index) => {
                if (message.role !== 'assistant') {
                    return traces;
                }
                const customerTurn = messages[index - 1];
                const storedSources = customerTurn && customerTurn.role === 'user' ? this.parseSources(customerTurn.sources) : [];
                if (!message.trace && !storedSources.length) {
                    return traces;
                }
                traces[message.id] = message.trace || {
                    legacy: true,
                    router: null,
                    sources: storedSources.map((source) => ({ title: source.title, type: String(source.external_id || '').replace(/_\d+$/, ''), score: null })),
                    toolCalls: [],
                    guard: [],
                    model: null,
                    system_prompt_hash: null
                };
                return traces;
            }, {});
        },
        conversationStore() {
            return useChatbotConversationStore();
        },
        conversation() {
            return this.conversationStore.currentConversation;
        },
        currentConversationLoading() {
            return this.conversationStore.currentConversationLoading;
        },
        isActive() {
            if (!this.conversation) return false;
            const expiresAt = this.conversation.expires_at;
            if (!expiresAt) return true;
            return expiresAt > Math.floor(Date.now() / 1000);
        },
        totalInputTokens() {
            const messages = (this.conversation && this.conversation.messages) || [];
            return messages.reduce((sum, message) => sum + (message.input_tokens || 0), 0);
        },
        // The sum of what the answers billed; null when none of them carries a figure (chats from before it was stored).
        totalBilledCost() {
            const billed = ((this.conversation && this.conversation.messages) || []).filter((message) => this.hasBilledCost(message));
            return billed.length ? billed.reduce((sum, message) => sum + message.billed_eur, 0) : null;
        },
        totalOutputTokens() {
            const messages = (this.conversation && this.conversation.messages) || [];
            return messages.reduce((sum, message) => sum + (message.output_tokens || 0), 0);
        }
    },

    methods: {
        // The cards the visitor saw under this answer (name, price, availability, image), stored with the message as JSON.
        productCards(message) {
            if (!message.attachments) {
                return [];
            }
            try {
                const attachments = JSON.parse(message.attachments);
                return Array.isArray(attachments.productCards) ? attachments.productCards : [];
            } catch (error) {
                return [];
            }
        },
        // A card whose image cannot be loaded keeps its neutral square instead of showing the alt text.
        hideBrokenImage(event) {
            event.target.style.display = 'none';
        },
        hasBilledCost(message) {
            return typeof message.billed_eur === 'number';
        },
        // An answer costs fractions of a cent: up to four decimals, so it never shows as 0,00 €.
        formatCost(amount) {
            return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(amount);
        },
        goBack() {
            this.$router.push({ name: 'chatbot-conversations' });
        },
        formatDate(timestamp) {
            if (!timestamp) return '—';
            return timestamp2date(timestamp, this.$t('yyyy-MM-dd') + ', HH:mm');
        },
        renderMarkdown(content) {
            if (!content) return '';
            const html = marked.parse(content);
            return DOMPurify.sanitize(html, { ADD_ATTR: ['target'] });
        },
        hasTokenInfo(message) {
            return !!(message.input_tokens || message.output_tokens || this.hasBilledCost(message));
        },
        wasCorrected(message) {
            return !!(message.trace && message.trace.guard && message.trace.guard.length);
        },
        isTraceOpen(messageId) {
            return !!this.openTraces[messageId];
        },
        toggleTrace(messageId) {
            this.$set(this.openTraces, messageId, !this.openTraces[messageId]);
        },
        parseSources(raw) {
            // The message sources arrive as JSON text.
            try {
                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
                return [];
            }
        },
        highlightParts(text, token) {
            const start = token ? text.indexOf(token) : -1;
            if (start < 0) {
                return [{ text, hit: false }];
            }
            return [
                { text: text.slice(0, start), hit: false },
                { text: token, hit: true },
                { text: text.slice(start + token.length), hit: false }
            ].filter((part) => part.text !== '');
        },
        formatExcerpt(excerpt) {
            // A tool result is stored as JSON text. A result cut off at the stored length is not valid JSON any more and stays as it is.
            try {
                return JSON.stringify(JSON.parse(excerpt), null, 2);
            } catch (error) {
                return excerpt;
            }
        },
        formatArguments(argumentList) {
            return argumentList.map((argument) => argument.key + ': ' + argument.value).join(', ');
        },
        evidenceKind(kind) {
            const kinds = { amount: 'Price', url: 'Link', email: 'E-mail', phone: 'Phone', address: 'Address' };
            return this.$t(kinds[kind] || kind);
        },
        openSource(message, index) {
            const trace = this.traces[message.id];
            const key = message.id + ':' + index;

            this.openedSource = { ...trace.sources[index], content: null };
            this.openedSourceKey = key;
            this.sourceDrawerVisible = true;

            // An answer from before the trace was recorded has no stored text.
            if (trace.legacy) {
                return;
            }

            this.openedSourceLoading = true;
            this.loadSourceTexts(message.id)
                .then(sources => {
                    if (this.openedSourceKey === key && sources[index]) {
                        this.openedSource = { ...this.openedSource, content: sources[index].content };
                    }
                })
                .catch(() => {})
                .then(() => {
                    if (this.openedSourceKey === key) {
                        this.openedSourceLoading = false;
                    }
                });
        },
        loadSourceTexts(messageId) {
            if (this.sourceTexts[messageId]) {
                return Promise.resolve(this.sourceTexts[messageId]);
            }

            return this.conversationStore.fetchMessage(messageId).then(message => {
                this.$set(this.sourceTexts, messageId, (message && message.trace && message.trace.sources) || []);
                return this.sourceTexts[messageId];
            });
        },
        sourceKind(type) {
            const kinds = {
                page: 'Page',
                product: 'Product',
                event: 'Event',
                manual: 'Entry',
                business_card: 'Business card',
                public_address: 'Address',
                section_meta: 'Section',
                reference: 'System prompt'
            };
            return this.$t(kinds[type] || type || 'Source');
        },
        formatScore(score) {
            return Number(score).toFixed(2);
        },
        actionLabel(action) {
            const labels = {
                repaired: 'Link repaired',
                unwrapped: 'Link kept as text',
                removed: 'Link removed',
                stripped: 'Detail removed'
            };
            return this.$t(labels[action.action] || action.action);
        }
    },

    mounted() {
        const id = this.$route.params.id;
        if (id) {
            this.conversationStore.fetchConversation(id)
                .catch(() => {
                    this.error = true;
                });
        }
    }
};
</script>

<style lang="sass" scoped>
.error,
.empty
    padding: 20px

.conversation-header
    margin-bottom: 24px

.session-id
    font-family: monospace
    font-size: 12px

.context-section
    margin-bottom: 24px

    .context-toggle
        margin-bottom: 8px

    .context-body
        background: #f5f7fa
        border: 1px solid #e4e7ed
        border-radius: 4px
        padding: 12px 16px
        font-size: 0.8125rem
        line-height: 1.5
        color: #606266

        pre
            margin: 0
            white-space: pre-wrap
            word-wrap: break-word
            font-family: inherit

.messages-container
    display: flex
    flex-direction: column
    gap: 12px

.product-cards
    display: flex
    flex-wrap: wrap
    gap: 8px
    margin-top: 10px

.product-card
    display: flex
    gap: 10px
    width: 260px
    padding: 8px
    background: #fff
    border: 1px solid #dcdfe6
    border-radius: 6px
    color: inherit
    text-decoration: none

    &:hover
        border-color: #409EFF

    &:focus-visible
        outline: 2px solid #409EFF
        outline-offset: 2px

    &__image
        flex: 0 0 56px
        height: 56px
        border-radius: 4px
        background: #f5f7fa
        overflow: hidden

        img
            width: 100%
            height: 100%
            object-fit: cover
            display: block

    &__info
        min-width: 0
        display: flex
        flex-direction: column
        gap: 2px

    &__name
        font-size: 13px
        line-height: 1.3
        font-weight: 600
        color: #303133
        display: -webkit-box
        -webkit-line-clamp: 2
        -webkit-box-orient: vertical
        overflow: hidden

    &__price
        font-size: 13px
        color: #303133

    &__availability
        display: flex
        align-items: center
        gap: 6px
        font-size: 12px

        &--in-stock
            color: #67c23a

            .product-card__dot
                background: #67c23a

        &--out-of-stock
            color: #909399

            .product-card__dot
                background: #c0c4cc

    &__dot
        width: 7px
        height: 7px
        border-radius: 50%

.message-bubble
    max-width: 75%
    padding: 10px 14px
    border-radius: 8px
    background: #f5f5f5

    &--user
        align-self: flex-end
        background: #ecf5ff
        border: 1px solid #d9ecff

    &--assistant
        align-self: flex-start
        background: #f0f9eb
        border: 1px solid #e1f3d8

    &__header
        display: flex
        justify-content: space-between
        align-items: center
        margin-bottom: 6px
        gap: 12px

    &__role
        font-weight: 600
        font-size: 12px
        text-transform: uppercase
        color: #666

    &__time
        font-size: 11px
        color: #999

    &__content
        word-break: break-word
        font-size: 14px
        line-height: 1.5

        &--plain
            white-space: pre-wrap

        ::v-deep p
            margin: 0 0 8px

            &:last-child
                margin-bottom: 0

        ::v-deep ul,
        ::v-deep ol
            margin: 0 0 8px
            padding-left: 20px

        ::v-deep code
            background: rgba(0, 0, 0, 0.05)
            padding: 2px 4px
            border-radius: 3px
            font-size: 0.875em

        ::v-deep pre
            background: rgba(0, 0, 0, 0.05)
            padding: 8px 12px
            border-radius: 4px
            overflow-x: auto
            margin: 0 0 8px

            code
                background: none
                padding: 0

        ::v-deep a
            color: #409eff
            text-decoration: none

            &:hover
                text-decoration: underline

    &__footer
        margin-top: 8px
        padding-top: 6px
        border-top: 1px dashed rgba(0, 0, 0, 0.08)
        display: flex
        gap: 12px
        font-size: 11px
        color: #909399

        .token-stat
            i
                margin-right: 2px

    &__footer
        flex-wrap: wrap
        align-items: center

        .trace-toggle
            margin-left: auto
            padding: 0

.trace
    margin: 10px 0 0
    background: #fff
    border: 1px solid #ebeef5
    border-radius: 6px
    font-size: 13px
    line-height: 1.5
    color: #303133
    animation: trace-open 0.15s ease-out

    &__section
        display: grid
        grid-template-columns: 96px 1fr
        gap: 4px 16px
        padding: 10px 14px

        + .trace__section
            border-top: 1px solid #f2f6fc

    &__notice
        padding: 8px 14px
        font-size: 12px
        color: #909399
        background: #fafafa
        border-bottom: 1px solid #f2f6fc
        border-radius: 6px 6px 0 0

    &__label
        margin: 0
        padding-top: 1px
        font-size: 12px
        font-weight: 500
        color: #909399

    &__body
        min-width: 0
        display: flex
        flex-wrap: wrap
        align-items: baseline
        gap: 4px 8px

    &__query
        font-weight: 500
        word-break: break-word

    &__empty
        color: #c0c4cc

    &__sources
        flex: 1 0 100%
        margin: 0
        padding: 0
        list-style: none

        li
            display: flex
            align-items: baseline
            gap: 10px
            padding: 2px 6px
            margin: 0 -6px
            border-radius: 4px
            cursor: pointer
            outline: none

            &:hover,
            &:focus-visible
                background: #f5f7fa

    &__kind
        flex: 0 0 auto
        min-width: 76px
        font-size: 12px
        color: #909399

    &__source-title
        flex: 1 1 auto
        min-width: 0
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap

    &__evidence
        flex: 1 0 100%
        margin: 0
        padding: 0
        list-style: none

        li
            display: flex
            align-items: baseline
            flex-wrap: wrap
            gap: 4px 10px
            padding: 2px 0

    &__fact
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace
        font-size: 12px
        font-weight: 600

    &__found
        font-size: 12px
        color: #606266
        word-break: break-word

    &__score
        flex: 0 0 auto
        font-size: 12px
        font-variant-numeric: tabular-nums
        color: #909399

    &__tool
        flex: 1 0 100%

    &__tool-name
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace
        font-size: 12px
        font-weight: 600

    &__arguments
        margin-left: 8px
        font-size: 12px
        color: #606266
        word-break: break-word

    &__excerpt
        flex: 1 0 100%
        margin: 4px 0 0
        padding: 6px 8px
        background: #f5f7fa
        border-radius: 4px
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace
        font-size: 11.5px
        line-height: 1.45
        color: #606266
        white-space: pre-wrap
        word-break: break-word

    &__correction
        flex: 1 0 100%
        display: flex
        flex-direction: column
        align-items: flex-start
        gap: 4px

        + .trace__correction
            margin-top: 8px

    &__context
        align-self: stretch
        display: flex
        flex-direction: column
        gap: 2px
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace
        font-size: 12px

    &__neighbour
        padding: 1px 8px 1px 24px
        color: #909399
        word-break: break-all

    &__hit
        font-weight: 700
        border-radius: 2px

    &__diff
        align-self: stretch
        padding: 3px 8px 3px 24px
        position: relative
        border-radius: 4px
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace
        font-size: 12px
        word-break: break-all

        &::before
            position: absolute
            left: 9px

        &--removed
            background: #fef0f0
            color: #c45656

            .trace__hit
                background: rgba(245, 108, 108, 0.25)

            &::before
                content: '-'

        &--gone
            padding-left: 8px
            background: #f4f4f5
            color: #909399
            font-style: italic

        &--added
            background: #f0f9eb
            color: #529b2e

            .trace__hit
                background: rgba(103, 194, 58, 0.25)

            &::before
                content: '+'

    &__meta
        gap: 2px 16px
        font-size: 12px
        color: #606266

    @media (max-width: 640px)
        &__section
            grid-template-columns: 1fr

.badge
    display: inline-block
    padding: 0 8px
    border-radius: 10px
    font-size: 12px
    line-height: 20px

    &--neutral
        background: #f4f4f5
        color: #606266

    &--success
        background: #f0f9eb
        color: #529b2e

    &--warning
        background: #fdf6ec
        color: #b88230

    &--danger
        background: #fef0f0
        color: #c45656

@keyframes trace-open
    from
        opacity: 0
        transform: translateY(-4px)

    to
        opacity: 1
        transform: none

.source-meta
    display: grid
    grid-template-columns: 90px 1fr
    gap: 6px 16px
    margin: 0 0 20px
    font-size: 13px

    dt
        color: #909399

    dd
        margin: 0
        min-width: 0
        word-break: break-word

.source-content-label
    margin: 0 0 8px
    font-size: 12px
    font-weight: 500
    color: #909399

.source-content
    margin: 0
    padding: 10px 12px
    background: #f5f7fa
    border-radius: 4px
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace
    font-size: 12px
    line-height: 1.5
    white-space: pre-wrap
    word-break: break-word

.source-missing
    font-size: 13px
    color: #909399

.form-buttons
    margin-top: 16px
    display: flex
    justify-content: flex-start
</style>
